import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { EsignEnvelopeType } from '/@/modules/esign/constants/EsignEnvelopeType';
import { EsignStatus } from '/@/modules/esign/constants/EsignStatus';

export class EsignEnvelope extends BaseEntityModel {
  /**
   * @var {number}
   */
  id;

  /**
   * @var {number}
   */
  docusignId;

  /**
   * @var {string}
   */
  status;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {EsignDocument[]}
   */
  esignDocuments;

  /**
   * @var {boolean}
   */
  canRequestUrl;

  /**
   * @relationship {EsignEnvelopeEstateAccount[]}
   */
  esignEnvelopeEstateAccounts;

  constructor(model = {}) {
    super(model);
    super.setRelationships(this, model, this.getModelName());

    this.id = model.id;
    this.docusignId = model.docusignId;
    this.status = model.status;
    this.type = model.type;
    this.canRequestUrl = model.canRequestUrl;
  }

  /**
   *
   * @returns {string}
   */
  getModelName() {
    return ModelType.ESIGN_ENVELOPE;
  }

  get executorEsignEnvelopeEstateAccounts() {
    return this.esignEnvelopeEstateAccounts?.filter(
      (envelopeEstateAccount) => envelopeEstateAccount.estateAccount.isExecutor
    );
  }

  get professionalEsignEnvelopeEstateAccounts() {
    return this.esignEnvelopeEstateAccounts?.filter(
      (envelopeEstateAccount) => envelopeEstateAccount.estateAccount.isProfessional
    );
  }

  /**
   * @returns {string}
   */
  get statusColor() {
    return EsignStatus.getByKey(this.status).color;
  }

  /**
   * @returns {boolean}
   */
  get isDistributionPlanType() {
    return this.type === EsignEnvelopeType.DISTRIBUTION_PLAN.key;
  }

  /**
   * @returns {boolean}
   */
  get isSent() {
    return this.status === EsignStatus.SENT.key;
  }

  /**
   * @returns {boolean}
   */
  get isDelivered() {
    return this.status === EsignStatus.DELIVERED.key;
  }

  /**
   * @returns {boolean}
   */
  get isVoided() {
    return this.status === EsignStatus.VOIDED.key;
  }

  /**
   * @returns {boolean}
   */
  get isDeclined() {
    return this.status === EsignStatus.DECLINED.key;
  }

  get isInterimDistributionReleaseType() {
    return this.type === EsignEnvelopeType.INTERIM_DISTRIBUTION_RELEASE.key;
  }

  /**
   * @returns {boolean}
   */
  get isCompleted() {
    return this.status === EsignStatus.COMPLETED.key;
  }

  /**
   * @returns {*}
   */
  get displayType() {
    return EsignEnvelopeType.getByKey(this.type);
  }

  /**
   * @returns {*}
   */
  get displayStatus() {
    return EsignStatus.getByKey(this.status);
  }

  /**
   * @param {EstateAccount} estateAccount
   * @returns {boolean}
   */
  hasCompleted(estateAccount) {
    const esignEnvelopeEstateAccount = this.esignEnvelopeEstateAccounts.find(
      (esignEnvelopeEstateAccount) =>
        esignEnvelopeEstateAccount.estateAccountId === estateAccount.id
    );

    return esignEnvelopeEstateAccount?.isCompleted;
  }

  /**
   * @param {EstateAccount} estateAccount
   * @returns {boolean}
   */
  hasSignedOrCompleted(estateAccount) {
    const esignEnvelopeEstateAccount = this.esignEnvelopeEstateAccounts.find(
      (esignEnvelopeEstateAccount) =>
        esignEnvelopeEstateAccount.estateAccountId === estateAccount.id
    );

    return esignEnvelopeEstateAccount?.isSignedOrCompleted;
  }

  recipientCanSign(estateAccountId) {
    const esignEnvelopeEstateAccount = this.esignEnvelopeEstateAccounts.find(
      (esignEnvelopeEstateAccount) => esignEnvelopeEstateAccount.estateAccountId === estateAccountId
    );

    return esignEnvelopeEstateAccount.canRequestUrl;
  }
}
